import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import housesService from '../../../services/housesService';
import pdfSignService, { PDFSignRequest } from '../../../services/pdfSignService';
import { House } from '../../../services/types';

const ESign = () => {
    // State for houses
    const [houses, setHouses] = useState<House[]>([]);
    const [loadingHouses, setLoadingHouses] = useState(true);

    // State for form
    const [formData, setFormData] = useState<PDFSignRequest>({
        house_id: "",
        document_type: 'check_in',
        language: 'es',
        deposit_amount: 150,
        client_name: '',
        additional_notes: '',
        date: '',
    });

    // State for submission
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Load houses on component mount
    useEffect(() => {
        loadHouses();
    }, []);

    const loadHouses = async () => {
        try {
            setLoadingHouses(true);
            const housesData = await housesService.getAll();
            console.log("Loaded houses:", housesData);
            setHouses(housesData);

            // Auto-select first house if available
            if (housesData.length > 0 && formData.house_id === "") {
                const firstHouse = housesData[0];
                // Set house_id to the public_id value
                setFormData(prev => ({ ...prev, house_id: firstHouse.public_id || "" }));
            }
        } catch (err: any) {
            console.error('Error loading houses:', err);
            setError('Failed to load houses. Please try again.');
        } finally {
            setLoadingHouses(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: name === 'deposit_amount'
                ? parseFloat(value) || 0
                : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validation
        if (formData.house_id === "") {
            setError('Please select a property');
            return;
        }

        if (formData.deposit_amount <= 0) {
            setError('Deposit amount must be greater than 0');
            return;
        }

        try {
            setSubmitting(true);

            console.log("Submitting form data:", formData);
            // Generate and download PDF
            const response = await pdfSignService.generateAndDownload(formData);

            if (response.success) {
                setSuccess(`PDF generated successfully: ${response.filename}`);
                // Reset optional fields
                setFormData(prev => ({
                    ...prev,
                    client_name: '',
                    additional_notes: '',
                    date: '',
                }));
            } else {
                setError(response.error || 'Failed to generate PDF');
            }
        } catch (err: any) {
            console.error('Error generating PDF:', err);
            setError(err?.message || 'Failed to generate PDF. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const languageOptions = [
        { value: 'es', label: 'Spanish (Español)' },
        { value: 'en', label: 'English' },
        { value: 'nl', label: 'Dutch (Nederlands)' },
        { value: 'fr', label: 'French (Français)' },
    ];

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">E-Sign - Deposit Receipt Generator</h4>
                <div className="text-muted small">
                    Generate signed PDF deposit receipts
                </div>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                    {loadingHouses ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="mt-3 text-muted">Loading properties...</p>
                        </div>
                    ) : (
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Select Property <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="house_id"
                                            value={formData.house_id}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Choose a property...</option>
                                            {houses.map((house) => (
                                                <option
                                                    key={house.public_id}
                                                    value={house.public_id}
                                                >
                                                    {house.name || 'Unnamed Property'} - {house.address}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            The property address will be automatically included in the PDF
                                        </Form.Text>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Document Type <span className="text-danger">*</span></Form.Label>
                                        <div>
                                            <Form.Check
                                                inline
                                                type="radio"
                                                label="Check In"
                                                name="document_type"
                                                value="check_in"
                                                checked={formData.document_type === 'check_in'}
                                                onChange={handleInputChange}
                                                id="check_in"
                                            />
                                            <Form.Check
                                                inline
                                                type="radio"
                                                label="Check Out"
                                                name="document_type"
                                                value="check_out"
                                                checked={formData.document_type === 'check_out'}
                                                onChange={handleInputChange}
                                                id="check_out"
                                            />
                                        </div>
                                        <Form.Text className="text-muted">
                                            Select whether this is for check-in or check-out
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Document Language <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="language"
                                            value={formData.language}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {languageOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Deposit Amount (€) <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="deposit_amount"
                                            value={formData.deposit_amount}
                                            onChange={handleInputChange}
                                            min={0}
                                            step={0.01}
                                            required
                                            placeholder="150"
                                        />
                                        <Form.Text className="text-muted">
                                            Amount will be converted to words automatically
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Client Name (Optional)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_name"
                                            value={formData.client_name}
                                            onChange={handleInputChange}
                                            placeholder="John Doe"
                                        />
                                        <Form.Text className="text-muted">
                                            Will be included in the PDF if provided
                                        </Form.Text>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date (Optional)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleInputChange}
                                            placeholder="Leave empty for current date"
                                        />
                                        <Form.Text className="text-muted">
                                            Defaults to today's date if not specified
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-4">
                                        <Form.Label>Additional Notes (Optional)</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="additional_notes"
                                            value={formData.additional_notes}
                                            onChange={handleInputChange}
                                            placeholder="Any additional information..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-between align-items-center">
                                <div className="text-muted small">
                                    <span className="text-danger">*</span> Required fields
                                </div>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={submitting}
                                    size="lg"
                                >
                                    {submitting ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Generating PDF...
                                        </>
                                    ) : (
                                        <>
                                            <span className="me-2">📄</span>
                                            Generate & Download PDF
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
                <Card.Body className="p-4">
                    <h5 className="mb-3">📋 Information</h5>
                    <Row>
                        <Col md={6}>
                            <h6 className="text-muted mb-2">What is included in the PDF?</h6>
                            <ul className="small">
                                <li>Company information (Pure Key Homes)</li>
                                <li>Representative signatures (Pedro Simões & Ana Matias)</li>
                                <li>Property address and details</li>
                                <li>Deposit amount (in numbers and words)</li>
                                <li>Date and location</li>
                                <li>Guest signature line</li>
                            </ul>
                        </Col>
                        <Col md={6}>
                            <h6 className="text-muted mb-2">Supported Languages</h6>
                            <ul className="small">
                                <li><strong>Spanish:</strong> For Spanish-speaking guests</li>
                                <li><strong>English:</strong> For English-speaking guests</li>
                                <li><strong>Dutch:</strong> For Dutch-speaking guests</li>
                                <li><strong>French:</strong> For French-speaking guests</li>
                            </ul>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </div>
    );
};

export default ESign;
